import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AppleLogo, GooglePlayLogo } from '@phosphor-icons/react';
import { useMediaAsset } from '@hooks/use-media-asset';
import {
  NavBrand,
  NavBrandMark,
  NavBrandText,
  NavCtas,
  NavInner,
  NavOuter,
  StoreButton,
} from '@pages/landing-page.styles';
import { storeCtaProps } from './store-cta';

/*
 * The site's floating nav, as the homepage has it — the brand home and
 * both stores — for every page built like the homepage (the landing page,
 * the product pages). It turns opaque once the reader is `solidAfter`
 * screens down, and tells the page it sits in how far down it reaches
 * (--nav-foot), so BentoSections start below it. Measured rather than
 * assumed: the nav changes height when the brand icon arrives and at md.
 */
export const SiteNav: React.FC<{ solidAfter?: number }> = ({ solidAfter = 0.85 }) => {
  const { url: brandIconUrl } = useMediaAsset('house_shimmer_slate_icon_variation');
  const [solid, setSolid] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > window.innerHeight * solidAfter);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [solidAfter]);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const page = nav?.parentElement;
    if (!nav || !page) return undefined;
    const measure = () => {
      const foot = (parseFloat(getComputedStyle(nav).top) || 0) + nav.offsetHeight;
      page.style.setProperty('--nav-foot', `${Math.round(foot)}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  return (
    <NavOuter ref={navRef}>
      <NavInner $scrolled={solid}>
        <NavBrand to="/">
          {brandIconUrl && <NavBrandMark src={brandIconUrl} alt="" />}
          <NavBrandText>The Home CEO</NavBrandText>
        </NavBrand>
        <NavCtas>
          <StoreButton as="a" {...storeCtaProps('ios', 'nav')}>
            <AppleLogo weight="fill" /> iOS
          </StoreButton>
          <StoreButton as="a" {...storeCtaProps('android', 'nav')}>
            <GooglePlayLogo weight="fill" /> Android
          </StoreButton>
        </NavCtas>
      </NavInner>
    </NavOuter>
  );
};
